// The message HTML (sanitized by the server: no scripts, forms or frames) goes into an <iframe sandbox srcdoc>.
// Its own Content-Security-Policy blocks remote images and fonts unless the user allows them for the message, which
// also blocks tracking pixels. The sandbox has no allow-scripts; allow-same-origin only lets the app measure the
// content to size the frame and fit wide newsletters to the screen.
// text-size-adjust stops iOS Safari from enlarging the text of blocks wider than the screen: it enlarges the font but
// not a line-height given in px, so the lines of a paragraph print over each other. The app's own page sets it too.
// Mail built for a desktop width (a table of width="800") first gives up that width (fitFixedWidths); only what still
// does not fit is scaled down, so plain text mail is not shrunk to unreadable type.

/** True when the sender fixed a box's width in px: a width attribute ("800", "800px") or an inline (min-)width in px. */
export function hasFixedWidth(widthAttr: string | null, style: string | null): boolean {
  return /^\s*\d+(?:\.\d+)?\s*(?:px)?\s*$/i.test(widthAttr ?? "") || /(?:^|;)\s*(?:min-)?width\s*:\s*\d+(?:\.\d+)?px/i.test(style ?? "");
}

/** Tables, cells and boxes whose fixed px width is wider than the body's content area take the available width
 * instead (tables 100%, the rest auto), so text wraps at the screen's edge. Percent widths are left alone, so columns
 * keep their proportions. Measures all, then changes all: two layouts, not one per element. */
export function fitFixedWidths(doc: Document): void {
  const body = doc.body;
  const style = doc.defaultView?.getComputedStyle(body);
  const room = body.clientWidth - (style ? parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) : 0);
  const wide = [...body.querySelectorAll<HTMLElement>("table, td, th, div")].filter(
    (el) => hasFixedWidth(el.getAttribute("width"), el.getAttribute("style")) && el.getBoundingClientRect().width > room + 1,
  );
  for (const el of wide) {
    el.style.setProperty("width", el.tagName === "TABLE" ? "100%" : "auto", "important");
    el.style.setProperty("min-width", "0", "important");
  }
}

/** Remote content the CSP blocks by default: http(s) images, CSS url()s and srcsets. */
export function hasRemoteContent(html: string): boolean {
  return /\b(?:src|background|srcset)\s*=\s*["']?\s*(?:https?:)?\/\//i.test(html) || /url\(\s*["']?\s*(?:https?:)?\/\//i.test(html);
}

export function frameDocument(html: string, allowRemote: boolean): string {
  const img = allowRemote ? "data: https: http:" : "data:";
  const csp = `default-src 'none'; style-src 'unsafe-inline'; img-src ${img}; font-src data:${allowRemote ? " https:" : ""}; media-src 'none'`;
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<meta name="color-scheme" content="light">
<meta name="referrer" content="no-referrer">
<base target="_blank">
<style>
html{background:#fff;color:#1d1d1f;-webkit-text-size-adjust:100%;text-size-adjust:100%}
body{margin:0;padding:12px 14px;font:15px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;overflow-wrap:anywhere}
img{max-width:100%;height:auto}
pre{white-space:pre-wrap}
blockquote{margin:0 0 0 .6em;padding-left:.8em;border-left:3px solid #ccc;color:#555}
</style></head><body>${html}</body></html>`;
}
